import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useRef, useState } from "react"

import { useText } from "../i18n/use-text"
import { cx } from "../lib/cx"
import { initialsOf } from "../theme/master-color"
import { Counter } from "../ui/counter"
import styles from "./nav-icons.module.css"

import { Popover } from "./popover"
import type { NavEntry } from "./use-nav"
import { UserPanel } from "./user-panel"

/**
 * Иконочная навигация 56 px — раскладка узкого экрана (768–1199).
 *
 * ЗАЧЕМ ОНА ВООБЩЕ НУЖНА. К стойке часто прикручен старый монитор, а окно браузера
 * открыто на половину экрана (ТЗ §7.1). На 1024 боковая полоса в 200 px забирает пятую
 * часть ширины — ровно ту, которой не хватает журналу на шесть мастеров. Поэтому
 * навигация сжимается, но НЕ исчезает: спрятать её за «гамбургер» значит сделать переход
 * между разделами двумя действиями вместо одного у человека, которого прерывают каждые
 * две минуты.
 *
 * ПОЧЕМУ ДВЕ БУКВЫ, А НЕ ЗНАЧКИ. Значков на одиннадцать разделов кабинета не существует
 * в узнаваемом виде: «Каналы», «Тариф и оплата» и «Разработчикам» рисуются либо одинаково,
 * либо ребусом. Дизайнер выбрал две первые буквы названия; там, где правило давало
 * совпадение («Каталог» и «Каналы» — оба «Ка»), пара разведена первыми буквами слов:
 * Кт и Кн (сокращения лежат в словаре, рядом с названиями). Подсказка и полное
 * доступное имя остаются у каждой кнопки, но теперь они не единственное, чем пункты
 * различаются: подписи в столбце уникальны, и это закреплено проверкой.
 */
export function NavIcons(props: {
  readonly entries: readonly NavEntry[]
  readonly tenantName: string
  readonly userName: string
  readonly roleName: string
}): ReactNode {
  const t = useText()
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])
  const anchor = useRef<HTMLDivElement>(null)

  return (
    <nav className={styles.nav} aria-label="Разделы кабинета">
      <div className={styles.brand} title={props.tenantName}>
        p.
      </div>

      <div className={styles.list}>
        {props.entries.map((entry) => (
          <Link
            key={entry.section.id}
            to={entry.section.path}
            title={t(entry.section.textKey)}
            // aria-label ПЕРЕВЕДЁН. Он оставался русским, пока подсказка переводилась:
            // человек, читающий интерфейс по-английски, слышал от экранного диктора
            // русское название — то есть единственный канал, по которому у него есть
            // полное название пункта, оставался непереведённым.
            aria-label={t(entry.section.textKey)}
            aria-current={entry.active ? "page" : undefined}
            className={cx(styles.item, entry.active ? styles.itemActive : undefined)}
          >
            <span aria-hidden="true">{t(entry.section.shortKey)}</span>
            {entry.badge === undefined ? null : (
              <Counter value={entry.badge} label="требуют действия" tone="attention" corner />
            )}
            {entry.dot ? (
              <span className={styles.dot}>
                <span className="plnr-visually-hidden">требует внимания</span>
              </span>
            ) : null}
          </Link>
        ))}
      </div>

      {/* Подвал навигации не исчезает вместе с подписями: имя, роль и выход обязаны
          быть на месте в любой раскладке — здесь они за аватаром. */}
      <div className={styles.footer} ref={anchor}>
        <button
          type="button"
          className={styles.avatar}
          aria-expanded={menuOpen}
          aria-haspopup="dialog"
          title={`${props.userName} · ${props.roleName}`}
          onClick={() => {
            setMenuOpen((open) => !open)
          }}
        >
          <span aria-hidden="true">{initialsOf(props.userName)}</span>
          <span className="plnr-visually-hidden">
            {props.userName}, {props.roleName}. Профиль и выход
          </span>
        </button>
        <Popover
          open={menuOpen}
          onClose={closeMenu}
          label="Профиль"
          className={styles.menu}
        >
          <UserPanel userName={props.userName} roleName={props.roleName} />
        </Popover>
      </div>
    </nav>
  )
}
